import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import PageHeader from '@/components/feature/PageHeader';
import { articles } from '@/mocks/articles';

const safetySteps = [
  {
    icon: 'ri-links-line',
    title: 'Verify the link',
    desc: 'Make sure any link you open points to the product or category you expect. Be wary of shortened or suspicious links that do not match their description.',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Check the seller',
    desc: 'Compare the seller and their listing against what you ordered. Sellers and prices can vary, so confirm details before you commit.',
  },
  {
    icon: 'ri-camera-line',
    title: 'Review QC photos',
    desc: 'Inspect QC photos against the listing for color, logo, stitching and shape. Raise concerns early while you can still request changes.',
  },
  {
    icon: 'ri-ruler-line',
    title: 'Confirm sizing',
    desc: 'Compare measurements and size labels against the sizing chart and your own measurements. Sizing can vary between sellers and regions.',
  },
  {
    icon: 'ri-lock-line',
    title: 'Protect your account',
    desc: 'Use a strong, unique password and keep records of your orders. Be cautious of anyone asking for sensitive account details.',
  },
  {
    icon: 'ri-ship-line',
    title: 'Understand shipping risks',
    desc: 'Shipping depends on weight, destination and line. Customs and local delivery can add time or cost, so plan accordingly.',
  },
  {
    icon: 'ri-coupon-line',
    title: 'Watch for fake coupons',
    desc: 'Offers change frequently. Verify any coupon or discount claim against the platform before you rely on it.',
  },
  {
    icon: 'ri-alert-line',
    title: 'Know the counterfeit risk',
    desc: 'Be realistic about replica and counterfeit items. Understand what you are buying and the associated risks before you order.',
  },
];

export default function Safety() {
  const { t } = useTranslation();
  const relatedSlugs = ['allchinabuy-qc-guide', 'allchinabuy-shipping-guide', 'is-allchinabuy-legit'];
  const related = articles.filter((a) => relatedSlugs.includes(a.slug));

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <PageHeader
          titleKey="page.safety.title"
          subtitleKey="page.safety.subtitle"
          breadcrumbKey="nav.safety"
        />

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-20">
          <div className="max-w-3xl space-y-5">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
              Safety comes down to verification. This guide gives you a practical checklist you can follow before every order to reduce the most common buying risks, from link checks to shipping and counterfeit awareness.
            </p>
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
              This website is an independent informational resource and is not the official AllChinaBuy website. We cannot guarantee any product, seller, price or shipping outcome, so always confirm current details yourself.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {safetySteps.map((s) => (
              <div
                key={s.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <span className="flex w-11 h-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <h2 className="mt-4 font-heading text-base md:text-lg font-bold text-foreground-950">{s.title}</h2>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <p className="text-sm text-foreground-600 leading-relaxed">{t('footer.disclaimer')}</p>
          </div>

          <div className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Related reading</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden transition-all hover:border-primary-300 cursor-pointer"
                >
                  <div className="h-32 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      title={`${a.title} AllChinaBuy Spreadsheet`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                      {a.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}